import { ChangeDetectionStrategy, Component, computed, inject, Injector, resource, runInInjectionContext, ViewEncapsulation } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { EntityView, EntityViewKanban, EntityViewSort, EntityViewSortDirection, MetadataEntityData, MetadataEntityFieldData, MetadataEntityFieldDomain, PriorityUtility, WorkOrderStatusUtility } from '@jms/model';
import { KanbanColumn, KanbanColumnTemplateDirective, KanbanComponent, KanbanDbClickParams, KanbanEditorType, KanbanItemTemplateDirective, KanbanSwimlane, KanbanSwimlaneHeaderTemplateDirective } from '@workbuddy/ui/v2/components/kanban';
import { EntityManifestService } from '@workbuddy/services/entities';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { EntityBoardContext } from '../../context';
import { EntityBoardStore } from '../../state';
import { EntityCardComponent } from '../entity.card';
import { BoardEmptyComponent } from '../board.empty';
import { BoardRowEditorService } from '../board.editor';
import { resolveBoardRowId, resolveBoardRowScopePaths } from '../../reorder';
import { KanbanBucket, KANBAN_EMPTY_BUCKET_KEY, resolveKanbanBuckets } from './kanban.buckets';
import { resolveKanbanColumnOptions, resolveKanbanRuleColumns } from './kanban.columns';
import { formatBoardCount, formatBoardRecordCount, resolveBoardRecordNoun } from '../group.header';
import { kanbanPlacementsEqual, type KanbanPlacement } from './kanban.placements';
import { KanbanValueSetService } from './kanban.value.set.service';
import { KanbanSwimlaneHeaderComponent } from './kanban.swimlane.header';
import { PointerGesturesDirective } from '@workbuddy/ui/v2/directives/pointer.gestures';
import { boardKanbanBridgeStyles, boardKanbanStyleId } from './board.kanban.theme';

type GroupBucket<T = any> = {
  key: string;
  name: string;
  items: T[];
  sortOrder: number | null;
};

/** The single lane / single column a view that groups by nothing still has to render into. */
const SINGLE_LANE_KEY = 'all';
const SINGLE_COLUMN_KEY = 'all';
const EMPTY_PLACEMENTS: readonly KanbanPlacement[] = [];
/** Sentinel for a card no declared column covers — dropped rather than rendered anywhere. */
const UNPLACED_COLUMN_KEY = '__unplaced__';
/** Unlabelled pending columns, for a board whose column set is not known at all yet. */

/** Whether a rendered column already holds exactly these cards, in this order. */
function sameItemKeys(items: readonly any[] | undefined, placements: readonly KanbanPlacement[], keyOf: (item: any) => string): boolean {

  if ((items?.length ?? 0) !== placements.length) return false;

  return placements.every((placement, index) => keyOf(items![index]) === placement.key);
}

@Component({
  selector: 'board-kanban',
  templateUrl: './board.kanban.component.html',
  styleUrls: ['./board.kanban.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, KanbanComponent, KanbanColumnTemplateDirective, KanbanItemTemplateDirective, KanbanSwimlaneHeaderTemplateDirective, EntityCardComponent, BoardEmptyComponent, KanbanSwimlaneHeaderComponent, PointerGesturesDirective],
})
export class BoardKanbanComponent {
  protected readonly KanbanEditorType = KanbanEditorType;
  private readonly dateFormatter = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  private readonly document = inject(DOCUMENT);
  private readonly store = inject(EntityBoardStore);
  private readonly injector = inject(Injector);
  private readonly valueSetService = inject(KanbanValueSetService);
  private readonly rowEditorService = inject(BoardRowEditorService);
  private readonly entityManifests = inject(EntityManifestService, { optional: true });

  protected readonly $context = computed(() => this.store.$context() as EntityBoardContext);
  protected readonly $view = computed(() => this.store.$effectiveView());
  protected readonly $metadata = computed(() => this.store.$metadata() as MetadataEntityData | null);
  protected readonly $items = computed(() => this.store.$items());
  /** The page as a lookup, so a card resolves its record from a placement key without a scan. */
  protected readonly $itemsByKey = computed(() => this.store.$itemsByKey());

  protected readonly $selectedItem = computed(() => this.store.$selectedItem());
  /** Selection membership as a Set — see `isSelected`. Single-select lands in `$selectedItems` too. */
  protected readonly $selectedRowKeys = computed(() => this.store.$selectedRowKeys());
  protected readonly $cardConfig = computed(() => this.$view()?.kanban?.card ?? null);
  protected readonly $kanbanConfig = computed(() => this.$view()?.kanban ?? null);
  protected readonly $effectiveColumnPath = computed(() => this.getEffectiveColumnPath(this.$kanbanConfig()));
  protected readonly $effectiveSwimlanePath = computed(() => this.getEffectiveSwimlanePath(this.$kanbanConfig()));
  protected readonly $isCardMode = computed(() => !this.$effectiveColumnPath());
  /**
   * Lane headers are off while the first page is in flight: with no rows there is only the synthetic
   * "All" lane, and naming it would put a header on screen that no landed board ever shows.
   */
  protected readonly $showSwimlaneHeaders = computed(() => !this.$isCardMode() && !!this.$effectiveSwimlanePath() && !this.$pending());

  /** What one card IS at this board's grain — a Stages board counts stages, not the jobs that own them. */
  private readonly $recordNoun = computed(() => resolveBoardRecordNoun(this.$metadata(), this.$view()?.recordScope ?? null));
  private readonly $tailGroupRows = computed<number | null>(() => this.store.$tailGroupRows());

  private readonly $columnField = computed(() => this.resolveField(this.$metadata(), this.$effectiveColumnPath()));

  /**
   * The column field's full value set. Null until loaded, so the board renders data-driven columns
   * immediately and re-buckets onto the stable column set once the set arrives.
   */
  private readonly valueSetResource = resource({
    params: () => ({ field: this.$columnField(), entity: this.$context()?.entity ?? null }),
    loader: ({ params }) => this.valueSetService.load(params.field, params.entity),
  });

  private readonly $valueSet = computed(() => this.valueSetResource.value() ?? null);

  /**
   * The columns this view renders. The view's own definitions win; absent, every value in the set gets
   * its own column, so a board with no configured columns still shows the whole field.
   */
  private readonly $columnOptions = computed(() => resolveKanbanColumnOptions(this.$kanbanConfig()?.columns, this.$valueSet()));

  /**
   * The view's rule columns, so a row whose status the picker no longer offers is still placed.
   *
   * A DEACTIVATED status leaves the value set but not the data: its rows still carry the system
   * group the rule names, and without this they mint a stray column beside the rule column they
   * belong in.
   */
  private readonly $ruleColumns = computed(() => resolveKanbanRuleColumns(this.$kanbanConfig()?.columns));

  /**
   * The column SET every lane renders.
   *
   * A declared column set resolves against the field's VALUE SET, so until that lands the bucketer
   * can produce nothing at all. Falling back to the view's own column definitions means the board —
   * pending or landed — shows its real columns from the first paint, and rows landing only add cards
   * rather than re-flowing the board into a different shape.
   */
  private readonly $buckets = computed(() => {

    const resolved = this.resolveBuckets(this.$items(), this.$view());
    return resolved.length ? resolved : this.resolveDeclaredColumns();
  });

  /**
   * The board is waiting on its FIRST page, not empty.
   *
   * `board-empty` is a claim about the data ("no jobs match this view") and making it before the
   * server has answered is simply wrong — it flashed on every board open. Only a settled query with
   * zero rows is empty; anything still in flight with nothing to show is pending.
   */
  protected readonly $pending = computed(() => !!this.store.$itemsLoading?.());

  /**
   * The view's own column definitions as buckets, for a board whose value set has not landed.
   *
   * Labels only — with no value set nothing can be bucketed into them, so they stand empty until it
   * arrives. A view that declares no columns renders none rather than inventing placeholder ones; an
   * invented column would simply vanish when the real set arrived.
   */
  private resolveDeclaredColumns(): KanbanBucket[] {

    const declared = this.$kanbanConfig()?.columns ?? [];

    return declared.map(column => ({ key: column.id, name: column.label ?? column.id, color: column.color ?? null, items: [], count: 0, sortOrder: null, known: true }));
  }

  public constructor() {

    DesignSystem.Mount(this.document, boardKanbanStyleId, boardKanbanBridgeStyles);
  }

  /** The page in display order, so placements and card lookups agree on where a card sits in a column. */
  private readonly $sortedItems = computed(() => this.sortItems(this.$items() ?? [], this.resolveSort(this.$view())));

  /**
   * Where each card sits — keys only, with a custom equality so an edit that moves nothing emits
   * nothing. A renamed job re-renders that one card off `$itemsByKey`; the lanes never rebuild.
   */
  protected readonly $placements = computed<KanbanPlacement[]>(() => this.resolvePlacements(), { equal: kanbanPlacementsEqual });

  /**
   * Lanes and columns over placement keys.
   *
   * Lane and column objects are reused whenever their own key list is unchanged, so moving one card
   * between two lanes leaves every other lane the same object — which is what lets `w-kanban` keep
   * its collapse state and its DOM instead of re-creating the board.
   */
  protected readonly $swimlanes = computed<KanbanSwimlane[]>(() => this.buildSwimlanes(this.$placements()));

  /**
   * Row identity for selection and focus.
   *
   * At Secondary grain the rows are parent-shaped — every stage of one job carries the JOB's `_id` —
   * so comparing root ids selects every sibling stage at once. This is the same resolver the table and
   * list boards key their rows on, so all three agree on what one row is.
   */
  protected readonly $itemKey = computed(() => {

    const scopes = resolveBoardRowScopePaths(this.store.$contextRecordScopes(), this.$metadata());
    return (item: any) => resolveBoardRowId(item, scopes);
  });

  /**
   * A card tap is ONE state: the card highlights and the panel follows it.
   *
   * Selection and focus used to be separate — a selectable board highlighted without opening
   * anything, a panel board opened without highlighting — so the operator could not tell from the
   * board which card the panel was showing.
   *
   * A PLAIN TAP always couples the two, even on a board whose selection type is `multiple`. Multi-
   * select is reached by explicit gestures (a checkbox column, cmd/shift-click) which the kanban does
   * not offer at all, so treating a bare tap as "add to a bulk selection" only ever raised the bulk
   * bar over one card and left the panel shut. The selection it writes is IMPLICIT, which is what
   * keeps that bar down — see `withSelectionState.$showBulkActions`.
   *
   * Tapping the selected card again clears both, which closes a selection-driven quickview; tapping
   * another switches to it.
   *
   * A NULL item is that second tap, not a missing one: `w-kanban` owns its own toggle and reports a
   * deselect by emitting null. Dropping it here left the quickview stuck open on a card the operator
   * had just unpicked — invisible until the board started writing the selection back through
   * `[selectedItem]`, which is what gave the kanban a current item to toggle off in the first place.
   */
  protected onItemSelected(item: any): void {
    const context = this.$context();
    const interaction = context?.itemInteraction;

    if (!item) {
      if (interaction?.selectable || this.store.$panelKind()) {
        this.store.whenPanelCanLeave(() => {
          this.store.setSelectedItems([]);
          this.store.setFocusedItemId(null, 'card');
        });
      }
      return;
    }

    if (interaction?.selectable || this.store.$panelKind()) {
      const rowId = this.$itemKey()(item);
      const deselecting = rowId === this.store.$focusedItemId();

      this.store.whenPanelCanLeave(() => {
        this.store.setSelectedItems(deselecting ? [] : [item]);
        this.store.setFocusedItemId(deselecting ? null : rowId, 'card');
      });
      return;
    }

    if (interaction?.navigate) {
      // Injection context so manifest navigate callbacks may `inject()` (e.g. EntityInspectors).
      runInInjectionContext(this.injector, () => interaction.navigate!(item));
    }
  }

  /**
   * A lane header tap opens the record the lane GROUPS BY — a Stages board's Job, not its stages.
   *
   * The chevron keeps collapsing; this rides the header body via `wbPointerGestures`, so the two
   * gestures never compete. A lane whose parent has no panel resolves to null and the tap does
   * nothing, rather than opening a panel on a record that cannot render one.
   */
  protected onSwimlaneHeaderTap(swimlane: KanbanSwimlane): void {

    const item = swimlane?.data ?? null;
    if (!item) return;

    const rowId = this.$itemKey()(item);
    if (this.store.$resolvePanelTarget()({ source: 'row', item, rowId }) == null) return;

    const deselecting = rowId === this.store.$focusedItemId() && this.store.$focusedPanelSource() === 'row';
    this.store.whenPanelCanLeave(() => this.store.setFocusedItemId(deselecting ? null : rowId, 'row'));
  }

  /**
   * Double click opens the whole record.
   *
   * Single click is a board-local gesture — it selects, or drives the panel beside the collection.
   * Neither leaves the board, so on a panel board there is otherwise no way to reach the full record
   * from a card. The open path mirrors the other displays: the mount's own `navigate` wins, then its
   * `onExpand` overlay, then the manifest's double-click row editor (the same modal `board-table`
   * opens on `onRowDoubleClicked`).
   */
  protected onItemDblClicked(event: KanbanDbClickParams): void {
    const item = event?.item;
    if (!item) return;

    const interaction = this.$context()?.itemInteraction;

    if (interaction?.navigate) {
      // Injection context so manifest navigate callbacks may `inject()` (e.g. EntityInspectors).
      runInInjectionContext(this.injector, () => interaction.navigate!(item));
      return;
    }

    if (interaction?.onExpand) {
      void runInInjectionContext(this.injector, () => interaction.onExpand!(item));
      return;
    }

    this.openRowEditor(item);
  }

  /** The manifest's double-click modal, matching what `board-table` opens for the same gesture. */
  private openRowEditor(item: any): void {
    const entity = this.$context()?.entity;
    if (!entity) return;

    const modal = this.entityManifests?.board(entity, this.$context()).rowEditors?.modal;
    if (modal?.trigger !== 'doubleClick') return;

    void this.rowEditorService.openModal({ entity, record: item, editor: modal, onRefresh: () => this.store.refresh() });
  }

  /**
   * Called once per card per change-detection pass, so it must stay a lookup.
   *
   * The store derives `$selectedRowKeys` once per selection change; scanning `$selectedItems` and
   * re-resolving a row id on both sides of every comparison made this O(cards x selected) on every
   * CD pass and was what left the highlight trailing the click on a 50-card board.
   */
  protected isSelected(item: any): boolean {

    if (!item) return false;

    return this.$selectedRowKeys().has(this.$itemKey()(item));
  }

  /** Swatch for a column header. Falls back to the neutral token when the value carries no colour. */
  protected getColumnColor(column: KanbanColumn): string {

    return this.getBucketColor(column.data as KanbanBucket | undefined);
  }

  /**
   * The swatch straight off a bucket.
   *
   * Always a colour, never nothing: a stale rule column matching no live value carries no colour of
   * its own, and a header rendered without its dot reads as broken rather than neutral.
   */
  protected getBucketColor(bucket: KanbanBucket | undefined | null): string {

    return bucket?.color || 'var(--local-board-kanban-swatch-neutral)';
  }

  /**
   * The entity's own swimlane-header component for this row axis, when the manifest declares one.
   *
   * Undeclared is the normal answer, and it resolves to null — the board then renders the
   * metadata-driven header. Resolved lazily so an entity that declares nothing never pays an import.
   */
  private readonly rowHeaderResource = resource({
    params: () => ({ entity: this.$context()?.entity ?? null, rowField: this.$effectiveSwimlanePath() }),
    loader: ({ params }) => params.entity
      ? this.entityManifests?.loadKanbanRowHeaderComponent?.(params.entity, params.rowField) ?? Promise.resolve(null)
      : Promise.resolve(null),
  });

  protected readonly $rowHeaderComponent = computed(() => this.rowHeaderResource.value() ?? null);

  /**
   * Inputs for a manifest-declared row header, so an entity component takes the same contract the
   * fallback does without the board knowing anything about it.
   *
   * This set is a published contract — `ngComponentOutlet` throws on an input a component does not
   * declare, so an entity header written against it must keep working. The fallback's own extras
   * (the raw `count` behind the phrase) are bound in the template, not added here.
   */
  protected rowHeaderInputs(swimlane: KanbanSwimlane): Record<string, unknown> {

    return {
      row: swimlane.data ?? null,
      laneName: swimlane.name,
      countLabel: this.getSwimlaneCountLabel(swimlane),
      view: this.$view(),
      metadata: this.$metadata(),
      rowField: this.$effectiveSwimlanePath(),
    };
  }

  protected getSwimlaneTitle(swimlane: KanbanSwimlane): string {
    return swimlane.name;
  }

  /** The lane's first row — parent fields the header elevates ride on every unwound row of it. */
  protected getSwimlaneRow(swimlane: KanbanSwimlane): any {
    return swimlane.data ?? null;
  }

  protected getSwimlaneItemCount(swimlane: KanbanSwimlane): number {
    return swimlane.columns.reduce((count, column) => count + (column.items?.length ?? 0), 0);
  }

  /**
   * The lane header's count phrase.
   *
   * A real lane is WHOLE on the page — row-group paging admits a group only when its first row falls
   * in the window and then pads to the group's end — so its page count is its total. The no-value tail
   * is the one group that is not padded whole: it spans pages, so it reports `<page> of <group total>`
   * from the server's tail count and falls back to the page count alone when the server sent none.
   */
  protected getSwimlaneCountLabel(swimlane: KanbanSwimlane): string {
    const count = this.getSwimlaneItemCount(swimlane);
    const noun = this.$recordNoun();
    const tailRows = this.isEmptySwimlane(swimlane) ? this.$tailGroupRows() : null;

    if (tailRows == null || tailRows <= count) return formatBoardRecordCount(count, noun);

    return `${formatBoardCount(count)} of ${formatBoardRecordCount(tailRows, noun)}`;
  }

  /** Column header count, in the board's own record noun so both headers agree on what a card is. */
  protected getColumnCountLabel(count: number): string {
    return formatBoardRecordCount(count ?? 0, this.$recordNoun());
  }

  /**
   * The count chip's text. Blank while the first page is in flight: a zero there is a claim about
   * the data, and the server has not answered yet.
   */
  protected getColumnCountText(count: number): string {
    return this.$pending() ? '' : String(count ?? 0);
  }

  protected isEmptySwimlane(swimlane: KanbanSwimlane): boolean {
    return swimlane.key === KANBAN_EMPTY_BUCKET_KEY;
  }

  /** Buckets across the whole page — the column SET every swimlane renders. */
  private resolveBuckets(items: any[], view: EntityView | null) {

    const path = this.$effectiveColumnPath();
    const sortedItems = this.sortItems(items ?? [], this.resolveSort(view));

    return resolveKanbanBuckets({
      items: sortedItems,
      path,
      valueSet: this.$columnOptions(),
      getKey: value => this.getGroupKey(value),
      getLabel: value => this.stringifyValue(value),
      getSortOrder: value => this.getSortOrder(value),
      getColor: value => this.resolveValueColor(value),
      emptyLabel: `No ${(this.$columnField()?.displayName ?? this.$columnField()?.name ?? 'value').toLowerCase()}`,
      ruleColumns: this.$ruleColumns(),
      hasDeclaredColumns: !!this.$kanbanConfig()?.columns?.length,
    });
  }

  /**
   * Column colour for a bucket value, mirroring how entity-card resolves its footer tags: a stored
   * swatch wins, then the system-status/priority utilities, then a manifest presentation resolver.
   */
  private resolveValueColor(value: any): string | null {

    if (value == null) return null;
    if (typeof value === 'string') return this.resolveManifestColor(value) ?? null;
    if (typeof value.color === 'string' && value.color) return value.color;

    return PriorityUtility.getColor(value) ?? WorkOrderStatusUtility.getColor(value) ?? null;
  }

  private resolveManifestColor(value: string): string | null {

    const entity = this.$context()?.entity;
    const path = this.$effectiveColumnPath();
    if (!entity || !path || !this.entityManifests) return null;

    return this.entityManifests.tagStyle(entity, path, value, null)?.color ?? null;
  }

  /**
   * One placement per card, in display order.
   *
   * The bucket a card lands in is matched on the bucket's CLAIMED keys — a collapsed column
   * ("In Progress: All") claims each tenant status id as an alias, so keying a card on its raw
   * status would find no column at all.
   */
  private resolvePlacements(): KanbanPlacement[] {

    const columnPath = this.$effectiveColumnPath();
    const swimlanePath = this.$effectiveSwimlanePath();
    const keyOf = this.$itemKey();
    const columnKeyOf = this.buildColumnKeyResolver();

    return this.$sortedItems()
      .map(item => ({
        key: keyOf(item),
        columnKey: columnPath ? columnKeyOf(this.getGroupKey(this.getValueFromPath(item, columnPath))) : SINGLE_COLUMN_KEY,
        rowKey: swimlanePath ? this.getGroupKey(this.getValueFromPath(item, swimlanePath)) : SINGLE_LANE_KEY,
      }))
      // A declared column set is exhaustive: a card no column covers is hidden on purpose, and
      // keeping its placement would still count it in the lane header it has no card in.
      .filter(placement => placement.columnKey !== UNPLACED_COLUMN_KEY);
  }

  /**
   * Row key → bucket key, built once per pass so placement resolution stays a lookup per card.
   *
   * A key no bucket claims resolves to itself in default mode — the bucketer will have minted a
   * stray column for it — but to the unplaced sentinel when the view declares its columns, where no
   * such column exists and the card must not be rendered at all.
   */
  private buildColumnKeyResolver(): (rowKey: string) => string {

    const byClaimedKey = new Map<string, string>();

    for (const bucket of this.$buckets()) {
      for (const claimed of bucket.claimedKeys ?? [bucket.key]) byClaimedKey.set(claimed, bucket.key);
    }

    const declared = !!this.$kanbanConfig()?.columns?.length;
    return rowKey => byClaimedKey.get(rowKey) ?? (declared ? UNPLACED_COLUMN_KEY : rowKey);
  }

  /**
   * Lanes over placements, reusing the previous lane and column objects wherever their membership is
   * unchanged.
   *
   * Identity is the whole point: `w-kanban` holds lanes in a `linkedSignal`, and a lane that arrives
   * as a new object loses whatever collapse state the operator set on it. Reusing the object also
   * means Angular's `@for` leaves that lane's DOM — and every card in it — exactly where it was.
   */
  private buildSwimlanes(placements: readonly KanbanPlacement[]): KanbanSwimlane[] {

    const swimlanePath = this.$effectiveSwimlanePath();
    const buckets = this.$buckets();
    const previous = this.previousSwimlanes;
    const byLane = this.groupPlacementsByLane(placements);
    const laneOrder = this.resolveLaneOrder(placements, swimlanePath);

    const lanes = laneOrder.map(lane => {
      const priorLane = previous.get(lane.key);
      const columns = this.buildColumns(byLane.get(lane.key) ?? EMPTY_PLACEMENTS, buckets, priorLane?.columns);

      // Only the columns can change for a lane whose key and name are the same — hand back the very
      // same lane when they did not, so nothing downstream sees a change at all.
      if (priorLane && priorLane.name === lane.name && priorLane.columns === columns) return priorLane;

      return {
        key: lane.key,
        name: lane.name,
        data: lane.items[0] ?? null,
        columns,
        sortOrder: lane.sortOrder ?? undefined,
        // A lone lane still names the axis it groups by, so the header must survive the kanban's
        // "one lane needs no header" heuristic — a page carrying only the no-value tail is one lane.
        alwaysShowSwimlaneHeader: !!swimlanePath,
      } as KanbanSwimlane;
    });

    this.previousSwimlanes = new Map(lanes.map(lane => [lane.key!, lane]));
    return lanes;
  }

  /** Lane objects from the last build, keyed by lane key — the source of the identity reuse above. */
  private previousSwimlanes = new Map<string, KanbanSwimlane>();

  private groupPlacementsByLane(placements: readonly KanbanPlacement[]): Map<string, KanbanPlacement[]> {

    const byLane = new Map<string, KanbanPlacement[]>();

    for (const placement of placements) {
      const existing = byLane.get(placement.rowKey);
      if (existing) existing.push(placement);
      else byLane.set(placement.rowKey, [placement]);
    }

    return byLane;
  }

  /**
   * Lane identity, label and order.
   *
   * An empty page still renders one lane so the column headers stay on screen — an operator reading
   * "nothing is In Progress" needs the columns, not a bare empty state.
   */
  private resolveLaneOrder(placements: readonly KanbanPlacement[], swimlanePath: string | null): GroupBucket[] {

    if (!swimlanePath) return [{ key: SINGLE_LANE_KEY, name: 'All', items: [], sortOrder: null }];
    if (!placements.length) return [{ key: SINGLE_LANE_KEY, name: 'All', items: [], sortOrder: null }];

    const byKey = this.$itemsByKey();
    const rows = placements.map(placement => byKey.get(placement.key)).filter(Boolean);
    return this.groupItems(rows, swimlanePath, this.$metadata(), 'All');
  }

  private getEffectiveColumnPath(kanban: EntityViewKanban | null): string | null {

    return kanban?.column && kanban.column !== 'none' ? kanban.column as string : null;
  }

  private getEffectiveSwimlanePath(kanban: EntityViewKanban | null): string | null {

    return kanban?.row && kanban.row !== 'none' ? kanban.row as string : null;
  }

  private resolveSort(view: EntityView | null): EntityViewSort | null {

    const kanban = view?.kanban;
    return kanban?.sortBy ? { _id: kanban.sortBy, direction: kanban.sortOrder ?? EntityViewSortDirection.Asc } : null;
  }

  /**
   * Columns for ONE swimlane, projected onto the board-wide bucket set.
   *
   * Every lane emits the same keys in the same order — a lane with no items for a bucket still emits
   * that column as empty, because `buildColumns` in the kanban state utilities unions keys across lanes
   * and `calculateColumnWidth` sizes from that union. Omitting an empty cell would misalign the grid.
   *
   * A bucket is matched on its CLAIMED keys, not its own key. A collapsed column ("In Progress: All",
   * key `system:In Progress`) claims each tenant status id as an alias, so a lane keyed on the raw row
   * value would find nothing under the bucket's own key — the lane rendered empty and its header read
   * "0 jobs" while the row sat on the page.
   */
  private buildColumns(placements: readonly KanbanPlacement[], buckets: KanbanBucket[], priorColumns: KanbanColumn[] | undefined): KanbanColumn[] {

    const byColumn = new Map<string, KanbanPlacement[]>();

    for (const placement of placements) {
      const existing = byColumn.get(placement.columnKey);
      if (existing) existing.push(placement);
      else byColumn.set(placement.columnKey, [placement]);
    }

    const byKey = this.$itemsByKey();
    let changed = false;

    const columns = buckets.map((bucket, index) => {
      const cellPlacements = byColumn.get(bucket.key) ?? EMPTY_PLACEMENTS;
      const prior = priorColumns?.[index];
      const items = cellPlacements.map(placement => byKey.get(placement.key)).filter(Boolean);

      // Membership is what identity turns on: the SAME cards in the SAME order means the same
      // column object, even though a card's record may have been replaced by a data-only edit.
      if (prior && prior.key === bucket.key && prior.name === bucket.name && sameItemKeys(prior.items, cellPlacements, this.$itemKey())) {
        // The records themselves may have changed under an unchanged membership — keep the object
        // but hand the column the current ones, so a renamed card re-renders in place.
        prior.items = items;
        return prior;
      }

      changed = true;
      return {
        key: bucket.key,
        name: bucket.name,
        items,
        hasItems: items.length > 0,
        sortOrder: bucket.sortOrder ?? undefined,
        data: bucket,
      } as KanbanColumn;
    });

    // An unchanged column set returns the prior array, which is what lets `buildSwimlanes` hand back
    // the prior LANE and leave its DOM and collapse state alone.
    return !changed && priorColumns && priorColumns.length === columns.length ? priorColumns : columns;
  }

  private groupItems(items: any[], path: string | null | undefined, metadata: MetadataEntityData | null, fallbackName: string): GroupBucket[] {
    if (!path) {
      return [{ key: 'all', name: fallbackName, items, sortOrder: null }];
    }

    const field = this.resolveField(metadata, path);
    const buckets = new Map<string, GroupBucket>();

    for (const item of items) {
      const rawValue = this.getValueFromPath(item, path);
      const key = this.getGroupKey(rawValue);
      const existing = buckets.get(key);

      if (existing) {
        existing.items.push(item);
        continue;
      }

      buckets.set(key, {
        key,
        // An empty lane is named for the axis it is missing, not for the axis itself: without this
        // it would inherit `field.displayName` and read as a real value ("Stage Owner").
        name: key === KANBAN_EMPTY_BUCKET_KEY
          ? `No ${field?.displayName ?? fallbackName}`
          : this.stringifyValue(rawValue) || field?.displayName || fallbackName,
        items: [item],
        sortOrder: this.getSortOrder(rawValue),
      });
    }

    return [...buckets.values()].sort((a, b) => {
      // The no-value lane is always last — it mirrors the server, which pages it as a tail after
      // every real group, so a page can carry its rows while real lanes are still to come.
      if ((a.key === KANBAN_EMPTY_BUCKET_KEY) !== (b.key === KANBAN_EMPTY_BUCKET_KEY)) {
        return a.key === KANBAN_EMPTY_BUCKET_KEY ? 1 : -1;
      }

      if (a.sortOrder != null && b.sortOrder != null && a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }

      if (a.sortOrder != null) {
        return -1;
      }

      if (b.sortOrder != null) {
        return 1;
      }

      return a.name.localeCompare(b.name);
    });
  }

  private sortItems(items: any[], sort: EntityViewSort | null | undefined): any[] {
    const path = sort?._id;

    if (!path) {
      return [...items];
    }

    const direction = sort?.direction ?? EntityViewSortDirection.Asc;
    const multiplier = direction === EntityViewSortDirection.Desc ? -1 : 1;

    return [...items].sort((left, right) => {
      const a = this.toComparableValue(this.getValueFromPath(left, path));
      const b = this.toComparableValue(this.getValueFromPath(right, path));

      if (a == null && b == null) {
        return 0;
      }

      if (a == null) {
        return 1;
      }

      if (b == null) {
        return -1;
      }

      if (a < b) {
        return -1 * multiplier;
      }

      if (a > b) {
        return 1 * multiplier;
      }

      return 0;
    });
  }

  private resolveField(metadata: MetadataEntityData | null, path: string | null | undefined): MetadataEntityFieldData | null {
    if (!metadata || !path) {
      return null;
    }

    const domain = new MetadataEntityFieldDomain(metadata);
    return this.getFieldFromPath(Object.values(domain.uiFields), path);
  }

  private getFieldFromPath(fields: MetadataEntityFieldData[], path: string): MetadataEntityFieldData | null {
    const directField = fields.find(field => field.path === path || field.name === path);

    if (directField) {
      return directField;
    }

    const [parentPath, ...rest] = path.split('.');

    if (!parentPath || !rest.length) {
      return null;
    }

    const parentField = fields.find(field => field.path === parentPath || field.name === parentPath);
    const childFields = parentField?.typeData?.fields ?? [];
    return childFields.length ? this.getFieldFromPath(childFields, rest.join('.')) : null;
  }

  private getValueFromPath(item: any, path: string | null | undefined): any {
    if (!item || !path) {
      return null;
    }

    return path.split('.').reduce((acc, part) => acc?.[part], item);
  }

  /**
   * Bucket key for a value.
   *
   * `_id` first is what makes a picker option and an embedded reference agree — the WorkOrderStatus
   * picker returns `{ _id, name }` while the row stores `{ _id, systemName, sortOrder, systemStatus }`,
   * and the shared `_id` is the only field both carry.
   */
  private getGroupKey(value: any): string {
    if (value == null) {
      return KANBAN_EMPTY_BUCKET_KEY;
    }

    if (typeof value === 'object') {
      return value._id ?? value.id ?? value.systemName ?? value.name ?? JSON.stringify(value);
    }

    return String(value);
  }

  private getSortOrder(value: any): number | null {
    if (value && typeof value === 'object' && typeof value.sortOrder === 'number') {
      return value.sortOrder;
    }

    return null;
  }

  private toComparableValue(value: any): string | number | null {
    if (value == null) {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      return value.toLowerCase();
    }

    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    }

    if (Array.isArray(value)) {
      return value.map(item => this.stringifyValue(item)).join(', ').toLowerCase();
    }

    if (typeof value === 'object') {
      return this.stringifyValue(value).toLowerCase();
    }

    return String(value).toLowerCase();
  }

  private stringifyValue(value: any): string {
    if (value == null) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.map(item => this.stringifyValue(item)).filter(Boolean).join(', ');
    }

    if (typeof value === 'string') {
      return this.isIsoDateString(value) ? this.dateFormatter.format(new Date(value)) : value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
      const label = value.name ?? value.label ?? value.displayName ?? value.systemName ?? value.systemStatus ?? value.value;

      if (label != null && label !== '') {
        return String(label);
      }

      const address = [value.street, value.suburb, value.state, value.postcode].filter(Boolean).join(' ');

      if (address) {
        return address;
      }
    }

    return String(value);
  }

  private isIsoDateString(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
  }

}
